import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalysisConfig, updateAnalysisConfig, getAdminExternalAnalysisFlag, patchAdminExternalAnalysisFlag } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import ConfirmDialog from '../components/ConfirmDialog';

type ResetType = 'extract' | 'consolidate' | 'supplement' | 'pronounResolve' | 'advancedExtract' | 'advancedMerge' | 'advancedValidate' | 'assetStateMatch' | 'directorMappingSystem' | 'directorMappingSpec' | 'charKeyword' | 'sceneKeyword' | 'propKeyword' | 'castingTags' | 'stylingTags' | 'castingRank' | 'castingRankSpec' | 'analyticsSystem' | 'analyticsUser' | 'refPrompt' | 'refLangs';
type CardKey = ResetType | 'directorMapping';

const CollapsibleSection: React.FC<{
  title: string;
  desc?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, desc, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: '2.5rem', borderTop: '1px solid var(--border)', paddingTop: '1.5rem' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
      >
        <div>
          <h2 style={{ fontSize: '1.1rem', margin: 0 }}>{title}</h2>
          {desc && <p style={{ color: 'var(--text-3)', fontSize: '0.82rem', margin: '0.3rem 0 0' }}>{desc}</p>}
        </div>
        <span style={{ color: 'var(--text-3)', fontSize: '1.2rem', transition: 'transform 0.2s', transform: open ? 'rotate(0)' : 'rotate(-90deg)' }}>&#9660;</span>
      </div>
      {open && <div style={{ marginTop: '1rem' }}>{children}</div>}
    </div>
  );
};

const AnalysisConfig: React.FC = () => {
  const { t } = useTranslation('config');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // per-card saving/success state
  const [savingCard, setSavingCard] = useState<CardKey | null>(null);
  const [successCard, setSuccessCard] = useState<CardKey | null>(null);

  const [extractRules, setExtractRules] = useState('');
  const [consolidateRules, setConsolidateRules] = useState('');
  const [supplementRules, setSupplementRules] = useState('');
  const [pronounResolveSystemPrompt, setPronounResolveSystemPrompt] = useState('');

  const [defaultExtract, setDefaultExtract] = useState('');
  const [defaultConsolidate, setDefaultConsolidate] = useState('');
  const [defaultSupplement, setDefaultSupplement] = useState('');
  const [defaultPronounResolveSystemPrompt, setDefaultPronounResolveSystemPrompt] = useState('');

  const [assetJSONSpec, setAssetJSONSpec] = useState('');
  const [promptFormat, setPromptFormat] = useState('');

  const [advancedExtractRules, setAdvancedExtractRules] = useState('');
  const [advancedMergeRules, setAdvancedMergeRules] = useState('');
  const [advancedValidateRules, setAdvancedValidateRules] = useState('');
  const [defaultAdvancedExtract, setDefaultAdvancedExtract] = useState('');
  const [defaultAdvancedMerge, setDefaultAdvancedMerge] = useState('');
  const [defaultAdvancedValidate, setDefaultAdvancedValidate] = useState('');

  const [assetStateMatchRules, setAssetStateMatchRules] = useState('');
  const [defaultAssetStateMatch, setDefaultAssetStateMatch] = useState('');
  const [assetStateMatchFormatSpec, setAssetStateMatchFormatSpec] = useState('');
  const [thinkingStateMatch, setThinkingStateMatch] = useState(false);

  const [directorMarkMappingSystemPrompt, setDirectorMarkMappingSystemPrompt] = useState('');
  const [directorMarkMappingJSONSpec, setDirectorMarkMappingJSONSpec] = useState('');
  const [defaultDirectorMarkMappingSystemPrompt, setDefaultDirectorMarkMappingSystemPrompt] = useState('');
  const [defaultDirectorMarkMappingJSONSpec, setDefaultDirectorMarkMappingJSONSpec] = useState('');

  const [charKeywordRules, setCharKeywordRules] = useState('');
  const [sceneKeywordRules, setSceneKeywordRules] = useState('');
  const [propKeywordRules, setPropKeywordRules] = useState('');
  const [defaultCharKeyword, setDefaultCharKeyword] = useState('');
  const [defaultSceneKeyword, setDefaultSceneKeyword] = useState('');
  const [defaultPropKeyword, setDefaultPropKeyword] = useState('');
  const [assetKeywordFormatSpec, setAssetKeywordFormatSpec] = useState('');
  const [thinkingAssetKeyword, setThinkingAssetKeyword] = useState(false);

  const [castingTagsRules, setCastingTagsRules] = useState('');
  const [stylingTagsRules, setStylingTagsRules] = useState('');
  const [defaultCastingTags, setDefaultCastingTags] = useState('');
  const [defaultStylingTags, setDefaultStylingTags] = useState('');
  const [castingTagsFormatSpec, setCastingTagsFormatSpec] = useState('');
  const [stylingTagsFormatSpec, setStylingTagsFormatSpec] = useState('');

  const [castingRankRules, setCastingRankRules] = useState('');
  const [castingRankFormatSpec, setCastingRankFormatSpec] = useState('');
  const [defaultCastingRank, setDefaultCastingRank] = useState('');
  const [defaultCastingRankSpec, setDefaultCastingRankSpec] = useState('');

  const [analyticsSystemPrompt, setAnalyticsSystemPrompt] = useState('');
  const [analyticsUserPromptTemplate, setAnalyticsUserPromptTemplate] = useState('');
  const [defaultAnalyticsSystem, setDefaultAnalyticsSystem] = useState('');
  const [defaultAnalyticsUser, setDefaultAnalyticsUser] = useState('');

  const [refTranslationPrompt, setRefTranslationPrompt] = useState('');
  const [refTranslationLangs, setRefTranslationLangs] = useState('');
  const [defaultRefPrompt, setDefaultRefPrompt] = useState('');
  const [defaultRefLangs, setDefaultRefLangs] = useState('');

  const [thinkingExtract, setThinkingExtract] = useState(false);
  const [thinkingConsolidate, setThinkingConsolidate] = useState(false);
  const [thinkingSupplement, setThinkingSupplement] = useState(false);
  const [thinkingAdvExtract, setThinkingAdvExtract] = useState(false);
  const [thinkingAdvMerge, setThinkingAdvMerge] = useState(false);
  const [thinkingAdvValidate, setThinkingAdvValidate] = useState(false);
  const [thinkingPrompt, setThinkingPrompt] = useState(false);

  // confirm reset
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetType, setResetType] = useState<ResetType>('extract');

  const [useExternalAnalysis, setUseExternalAnalysis] = useState(false);
  const [savingExt, setSavingExt] = useState(false);

  // refs to always get latest state values in save callbacks
  const getLatest = useCallback(() => ({
    extractRules, consolidateRules, supplementRules,
    pronounResolveSystemPrompt,
    advancedExtractRules, advancedMergeRules, advancedValidateRules,
    thinkingExtract, thinkingConsolidate, thinkingSupplement,
    thinkingAdvExtract, thinkingAdvMerge, thinkingAdvValidate,
    thinkingPrompt, thinkingStateMatch,
    assetStateMatchRules, directorMarkMappingSystemPrompt, directorMarkMappingJSONSpec,
    charKeywordRules, sceneKeywordRules, propKeywordRules, thinkingAssetKeyword,
    castingTagsRules, stylingTagsRules,
    castingRankRules, castingRankFormatSpec,
    analyticsSystemPrompt, analyticsUserPromptTemplate,
    refTranslationPrompt, refTranslationLangs,
  }), [extractRules, consolidateRules, supplementRules, pronounResolveSystemPrompt, advancedExtractRules, advancedMergeRules, advancedValidateRules,
    thinkingExtract, thinkingConsolidate, thinkingSupplement, thinkingAdvExtract, thinkingAdvMerge, thinkingAdvValidate,
    thinkingPrompt, thinkingStateMatch, assetStateMatchRules, directorMarkMappingSystemPrompt, directorMarkMappingJSONSpec,
    charKeywordRules, sceneKeywordRules, propKeywordRules, thinkingAssetKeyword,
    castingTagsRules, stylingTagsRules,
    castingRankRules, castingRankFormatSpec,
    analyticsSystemPrompt, analyticsUserPromptTemplate,
    refTranslationPrompt, refTranslationLangs]);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getAnalysisConfig();
        setExtractRules(cfg.extractRules || cfg.defaultExtractRules || '');
        setConsolidateRules(cfg.consolidateRules || cfg.defaultConsolidateRules || '');
        setSupplementRules(cfg.supplementRules || cfg.defaultSupplementRules || '');
        setPronounResolveSystemPrompt(cfg.pronounResolveSystemPrompt || cfg.defaultPronounResolveSystemPrompt || '');
        setDefaultExtract(cfg.defaultExtractRules);
        setDefaultConsolidate(cfg.defaultConsolidateRules);
        setDefaultSupplement(cfg.defaultSupplementRules);
        setDefaultPronounResolveSystemPrompt(cfg.defaultPronounResolveSystemPrompt || '');
        setAssetJSONSpec(cfg.assetJSONSpec || cfg.advancedFormatSpec || '');
        setPromptFormat(cfg.promptFormatSpec || '');
        setAdvancedExtractRules(cfg.advancedExtractRules || cfg.defaultAdvancedExtractRules || '');
        setAdvancedMergeRules(cfg.advancedMergeRules || cfg.defaultAdvancedMergeRules || '');
        setAdvancedValidateRules(cfg.advancedValidateRules || cfg.defaultAdvancedValidateRules || '');
        setDefaultAdvancedExtract(cfg.defaultAdvancedExtractRules || '');
        setDefaultAdvancedMerge(cfg.defaultAdvancedMergeRules || '');
        setDefaultAdvancedValidate(cfg.defaultAdvancedValidateRules || '');
        setThinkingExtract(!!cfg.thinkingExtract);
        setThinkingConsolidate(!!cfg.thinkingConsolidate);
        setThinkingSupplement(!!cfg.thinkingSupplement);
        setThinkingAdvExtract(!!cfg.thinkingAdvExtract);
        setThinkingAdvMerge(!!cfg.thinkingAdvMerge);
        setThinkingAdvValidate(!!cfg.thinkingAdvValidate);
        setThinkingPrompt(!!cfg.thinkingPrompt);
        setThinkingStateMatch(!!cfg.thinkingStateMatch);
        setAssetStateMatchRules(cfg.assetStateMatchRules || cfg.defaultAssetStateMatchRules || '');
        setDefaultAssetStateMatch(cfg.defaultAssetStateMatchRules || '');
        setAssetStateMatchFormatSpec(cfg.assetStateMatchFormatSpec || '');
        setDirectorMarkMappingSystemPrompt(cfg.directorMarkMappingSystemPrompt || cfg.defaultDirectorMarkMappingSystemPrompt || '');
        setDirectorMarkMappingJSONSpec(cfg.directorMarkMappingJSONSpec || cfg.defaultDirectorMarkMappingJSONSpec || '');
        setDefaultDirectorMarkMappingSystemPrompt(cfg.defaultDirectorMarkMappingSystemPrompt || '');
        setDefaultDirectorMarkMappingJSONSpec(cfg.defaultDirectorMarkMappingJSONSpec || '');
        setCharKeywordRules(cfg.characterKeywordRules || cfg.defaultCharacterKeywordRules || '');
        setSceneKeywordRules(cfg.sceneKeywordRules || cfg.defaultSceneKeywordRules || '');
        setPropKeywordRules(cfg.propKeywordRules || cfg.defaultPropKeywordRules || '');
        setDefaultCharKeyword(cfg.defaultCharacterKeywordRules || '');
        setDefaultSceneKeyword(cfg.defaultSceneKeywordRules || '');
        setDefaultPropKeyword(cfg.defaultPropKeywordRules || '');
        setAssetKeywordFormatSpec(cfg.assetKeywordFormatSpec || '');
        setThinkingAssetKeyword(!!cfg.thinkingAssetKeyword);
        setCastingTagsRules(cfg.castingTagsRules || cfg.defaultCastingTagsRules || '');
        setStylingTagsRules(cfg.stylingTagsRules || cfg.defaultStylingTagsRules || '');
        setDefaultCastingTags(cfg.defaultCastingTagsRules || '');
        setDefaultStylingTags(cfg.defaultStylingTagsRules || '');
        setCastingTagsFormatSpec(cfg.castingTagsFormatSpec || '');
        setStylingTagsFormatSpec(cfg.stylingTagsFormatSpec || '');
        setCastingRankRules(cfg.castingRankRules || cfg.defaultCastingRankRules || '');
        setCastingRankFormatSpec(cfg.castingRankFormatSpec || cfg.defaultCastingRankFormatSpec || '');
        setDefaultCastingRank(cfg.defaultCastingRankRules || '');
        setDefaultCastingRankSpec(cfg.defaultCastingRankFormatSpec || '');
        setAnalyticsSystemPrompt(cfg.analyticsSystemPrompt || cfg.defaultAnalyticsSystemPrompt || '');
        setAnalyticsUserPromptTemplate(cfg.analyticsUserPromptTemplate || cfg.defaultAnalyticsUserPromptTemplate || '');
        setDefaultAnalyticsSystem(cfg.defaultAnalyticsSystemPrompt || '');
        setDefaultAnalyticsUser(cfg.defaultAnalyticsUserPromptTemplate || '');
        setRefTranslationPrompt(cfg.refTranslationPrompt || cfg.defaultRefTranslationPrompt || '');
        setRefTranslationLangs(cfg.refTranslationLangs || cfg.defaultRefTranslationLangs || '');
        setDefaultRefPrompt(cfg.defaultRefTranslationPrompt || '');
        setDefaultRefLangs(cfg.defaultRefTranslationLangs || '');
      } catch (err: any) {
        setError(err.message || t('analysis.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAdminExternalAnalysisFlag()
      .then((r) => {
        if (!cancelled) setUseExternalAnalysis(r.useExternalAnalysis);
      })
      .catch(() => {
        // silently ignore
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExternalAnalysisToggle = useCallback(async (next: boolean) => {
    setSavingExt(true);
    try {
      const r = await patchAdminExternalAnalysisFlag(next);
      setUseExternalAnalysis(r.useExternalAnalysis);
    } catch {
      alert(t('analysis.saveFailed'));
    } finally {
      setSavingExt(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleSaveCard = async (card: CardKey) => {
    setSavingCard(card);
    setError('');
    setSuccessCard(null);
    try {
      const latest = getLatest();
      await updateAnalysisConfig({
        extractRules: latest.extractRules.trim(),
        consolidateRules: latest.consolidateRules.trim(),
        supplementRules: latest.supplementRules.trim(),
        pronounResolveSystemPrompt: latest.pronounResolveSystemPrompt.trim(),
        advancedExtractRules: latest.advancedExtractRules.trim(),
        advancedMergeRules: latest.advancedMergeRules.trim(),
        advancedValidateRules: latest.advancedValidateRules.trim(),
        thinkingExtract: latest.thinkingExtract,
        thinkingConsolidate: latest.thinkingConsolidate,
        thinkingSupplement: latest.thinkingSupplement,
        thinkingAdvExtract: latest.thinkingAdvExtract,
        thinkingAdvMerge: latest.thinkingAdvMerge,
        thinkingAdvValidate: latest.thinkingAdvValidate,
        thinkingPrompt: latest.thinkingPrompt,
        thinkingStateMatch: latest.thinkingStateMatch,
        assetStateMatchRules: latest.assetStateMatchRules.trim(),
        directorMarkMappingSystemPrompt: latest.directorMarkMappingSystemPrompt.trim(),
        directorMarkMappingJSONSpec: latest.directorMarkMappingJSONSpec.trim(),
        characterKeywordRules: latest.charKeywordRules.trim(),
        sceneKeywordRules: latest.sceneKeywordRules.trim(),
        propKeywordRules: latest.propKeywordRules.trim(),
        thinkingAssetKeyword: latest.thinkingAssetKeyword,
        castingTagsRules: latest.castingTagsRules.trim(),
        stylingTagsRules: latest.stylingTagsRules.trim(),
        castingRankRules: latest.castingRankRules.trim(),
        castingRankFormatSpec: latest.castingRankFormatSpec.trim(),
        analyticsSystemPrompt: latest.analyticsSystemPrompt.trim(),
        analyticsUserPromptTemplate: latest.analyticsUserPromptTemplate.trim(),
        refTranslationPrompt: latest.refTranslationPrompt,
        refTranslationLangs: latest.refTranslationLangs,
      });
      setSuccessCard(card);
      setTimeout(() => setSuccessCard(prev => prev === card ? null : prev), 2000);
    } catch (err: any) {
      setError(err.message || t('analysis.saveFailed'));
    } finally {
      setSavingCard(null);
    }
  };

  const handleReset = (type: ResetType) => {
    setResetType(type);
    setConfirmReset(true);
  };

  const doReset = () => {
    switch (resetType) {
      case 'extract': setExtractRules(defaultExtract); break;
      case 'consolidate': setConsolidateRules(defaultConsolidate); break;
      case 'supplement': setSupplementRules(defaultSupplement); break;
      case 'pronounResolve': setPronounResolveSystemPrompt(defaultPronounResolveSystemPrompt); break;
      case 'advancedExtract': setAdvancedExtractRules(defaultAdvancedExtract); break;
      case 'advancedMerge': setAdvancedMergeRules(defaultAdvancedMerge); break;
      case 'advancedValidate': setAdvancedValidateRules(defaultAdvancedValidate); break;
      case 'assetStateMatch': setAssetStateMatchRules(defaultAssetStateMatch); break;
      case 'directorMappingSystem': setDirectorMarkMappingSystemPrompt(defaultDirectorMarkMappingSystemPrompt); break;
      case 'directorMappingSpec': setDirectorMarkMappingJSONSpec(defaultDirectorMarkMappingJSONSpec); break;
      case 'charKeyword': setCharKeywordRules(defaultCharKeyword); break;
      case 'sceneKeyword': setSceneKeywordRules(defaultSceneKeyword); break;
      case 'propKeyword': setPropKeywordRules(defaultPropKeyword); break;
      case 'castingTags': setCastingTagsRules(defaultCastingTags); break;
      case 'stylingTags': setStylingTagsRules(defaultStylingTags); break;
      case 'castingRank': setCastingRankRules(defaultCastingRank); break;
      case 'castingRankSpec': setCastingRankFormatSpec(defaultCastingRankSpec); break;
      case 'analyticsSystem': setAnalyticsSystemPrompt(defaultAnalyticsSystem); break;
      case 'analyticsUser': setAnalyticsUserPromptTemplate(defaultAnalyticsUser); break;
      case 'refPrompt': setRefTranslationPrompt(defaultRefPrompt); break;
      case 'refLangs': setRefTranslationLangs(defaultRefLangs); break;
    }
    setConfirmReset(false);
  };

  const readonlyStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '80px',
    fontSize: '0.82rem',
    fontFamily: 'inherit',
    lineHeight: 1.6,
    border: '1px solid var(--border)',
    borderRadius: '6px',
    padding: '0.75rem',
    resize: 'vertical',
    boxSizing: 'border-box',
    background: 'var(--surface-2)',
    color: 'var(--text-2)',
    cursor: 'not-allowed',
  };

  const cardFooter = (card: CardKey) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
      {successCard === card && <span style={{ color: 'var(--success)', fontSize: '0.82rem' }}>{t('analysis.saved')}</span>}
      <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '4px 16px' }} onClick={() => handleSaveCard(card)} disabled={savingCard !== null}>
        {savingCard === card ? t('analysis.saving') : t('analysis.save')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1rem' }}>
        <div className="empty-state">
          <div className="loading-spinner" style={{ width: '32px', height: '32px', borderWidth: '3px' }} />
          <p>{t('analysis.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1rem' }}>
      <Breadcrumb items={[{ label: t('analysis.breadcrumbHome'), to: '/projects' }, { label: t('analysis.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('analysis.pageTitle')}</h1>
      </div>

      <div
        style={{
          marginTop: '1.5rem',
          padding: '1rem',
          border: '1px solid var(--border)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: '1.05rem', margin: 0 }}>{t('analysis.externalAnalysisTitle')}</h2>
          <p style={{ color: 'var(--text-3)', fontSize: '0.82rem', margin: '0.4rem 0 0' }}>
            {t('analysis.externalAnalysisDesc')}
          </p>
        </div>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            flexShrink: 0,
          }}
        >
          {savingExt && (
            <span style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>{t('analysis.externalSaving')}</span>
          )}
          <span
            style={{
              fontSize: '0.78rem',
              color: useExternalAnalysis ? 'var(--primary)' : 'var(--text-3)',
              fontWeight: 500,
            }}
          >
            {useExternalAnalysis ? t('analysis.externalEnabled') : t('analysis.externalDisabled')}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={useExternalAnalysis}
            aria-label={t('analysis.externalAnalysisLabel')}
            disabled={savingExt}
            onClick={() => handleExternalAnalysisToggle(!useExternalAnalysis)}
            style={{
              position: 'relative',
              width: 44,
              height: 24,
              padding: 0,
              border: 'none',
              borderRadius: 9999,
              background: useExternalAnalysis ? 'var(--primary)' : '#d1d5db',
              cursor: savingExt ? 'not-allowed' : 'pointer',
              opacity: savingExt ? 0.6 : 1,
              transition: 'background 0.15s ease',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 2,
                left: useExternalAnalysis ? 22 : 2,
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                transition: 'left 0.15s ease',
              }}
            />
          </button>
        </div>
      </div>

      <p style={{ color: 'var(--text-3)', fontSize: '0.82rem', marginBottom: '1.5rem' }}>
        {t('analysis.configDesc')}
      </p>

      {error && <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</p>}

      {/* ====== Basic Asset Analysis ====== */}
      <CollapsibleSection
        title={t('analysis.basicAnalysis')}
        desc={t('analysis.basicAnalysisDesc')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.extractTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('extract')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.extractDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '140px', fontSize: '0.82rem' }}
              value={extractRules}
              onChange={(e) => setExtractRules(e.target.value)}
            />
            {cardFooter('extract')}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.consolidateTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('consolidate')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.consolidateDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '140px', fontSize: '0.82rem' }}
              value={consolidateRules}
              onChange={(e) => setConsolidateRules(e.target.value)}
            />
            {cardFooter('consolidate')}
          </div>
        </div>
      </CollapsibleSection>

      {/* ====== Advanced Asset Analysis ====== */}
      <CollapsibleSection
        title={t('analysis.advancedAnalysis')}
        desc={t('analysis.advancedAnalysisDesc')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.advExtractTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('advancedExtract')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.advExtractDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '160px', fontSize: '0.82rem' }}
              value={advancedExtractRules}
              onChange={(e) => setAdvancedExtractRules(e.target.value)}
            />
            {cardFooter('advancedExtract')}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.advMergeTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('advancedMerge')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.advMergeDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '140px', fontSize: '0.82rem' }}
              value={advancedMergeRules}
              onChange={(e) => setAdvancedMergeRules(e.target.value)}
            />
            {cardFooter('advancedMerge')}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.advValidateTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('advancedValidate')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.advValidateDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '160px', fontSize: '0.82rem' }}
              value={advancedValidateRules}
              onChange={(e) => setAdvancedValidateRules(e.target.value)}
            />
            {cardFooter('advancedValidate')}
          </div>
        </div>
      </CollapsibleSection>

      {/* ====== Pronoun Resolution ====== */}
      <CollapsibleSection
        title={t('analysis.pronounResolveSection')}
        desc={t('analysis.pronounResolveSectionDesc')}
      >
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.pronounResolveTitle')}</h3>
            <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('pronounResolve')}>
              {t('analysis.resetDefault')}
            </button>
          </div>
          <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
            {t('analysis.pronounResolveDesc')}
          </p>
          <textarea
            className="input textarea"
            style={{ minHeight: '180px', fontSize: '0.82rem' }}
            value={pronounResolveSystemPrompt}
            onChange={(e) => setPronounResolveSystemPrompt(e.target.value)}
          />
          {cardFooter('pronounResolve')}
        </div>
      </CollapsibleSection>

      {/* ====== Gap-Fill ====== */}
      <CollapsibleSection
        title={t('analysis.supplementSection')}
        desc={t('analysis.supplementSectionDesc')}
      >
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.supplementTitle')}</h3>
            <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('supplement')}>
              {t('analysis.resetDefault')}
            </button>
          </div>
          <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
            {t('analysis.supplementDesc')}
          </p>
          <textarea
            className="input textarea"
            style={{ minHeight: '140px', fontSize: '0.82rem' }}
            value={supplementRules}
            onChange={(e) => setSupplementRules(e.target.value)}
          />
          {cardFooter('supplement')}
        </div>
      </CollapsibleSection>

      {/* ====== Asset State Matching ====== */}
      <CollapsibleSection
        title={t('analysis.stateMatchSection')}
        desc={t('analysis.stateMatchSectionDesc')}
      >
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.stateMatchTitle')}</h3>
            <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('assetStateMatch')}>
              {t('analysis.resetDefault')}
            </button>
          </div>
          <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
            {t('analysis.stateMatchDesc')}
          </p>
          <textarea
            className="input textarea"
            style={{ minHeight: '160px', fontSize: '0.82rem' }}
            value={assetStateMatchRules}
            onChange={(e) => setAssetStateMatchRules(e.target.value)}
          />
          {cardFooter('assetStateMatch')}

          {assetStateMatchFormatSpec && (
            <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
              <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.85rem', color: 'var(--text-2)' }}>{t('analysis.stateMatchFormatTitle')}</h4>
              <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
                {t('analysis.stateMatchFormatDesc')}
              </p>
              <textarea style={readonlyStyle} value={assetStateMatchFormatSpec.trim()} readOnly rows={10} />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* ====== Director Annotation Mapping ====== */}
      <CollapsibleSection
        title={t('analysis.directorMappingSection')}
        desc={t('analysis.directorMappingSectionDesc')}
      >
        <div className="card">
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.directorMappingSystemTitle')}</h3>
              <button
                className="btn btn-ghost"
                aria-label={t('analysis.resetDirectorMappingSystem')}
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={() => handleReset('directorMappingSystem')}
              >
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.directorMappingSystemDesc')}
            </p>
            <textarea
              aria-label={t('analysis.directorMappingSystemTitle')}
              className="input textarea"
              style={{ minHeight: '160px', fontSize: '0.82rem' }}
              value={directorMarkMappingSystemPrompt}
              onChange={(event) => setDirectorMarkMappingSystemPrompt(event.target.value)}
            />
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.directorMappingSpecTitle')}</h3>
              <button
                className="btn btn-ghost"
                aria-label={t('analysis.resetDirectorMappingSpec')}
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={() => handleReset('directorMappingSpec')}
              >
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.directorMappingSpecDesc')}
            </p>
            <textarea
              aria-label={t('analysis.directorMappingSpecTitle')}
              className="input textarea"
              style={{ minHeight: '160px', fontSize: '0.82rem', fontFamily: 'monospace' }}
              value={directorMarkMappingJSONSpec}
              onChange={(event) => setDirectorMarkMappingJSONSpec(event.target.value)}
            />
          </div>
          {cardFooter('directorMapping')}
        </div>
      </CollapsibleSection>

      {/* ====== Asset Search Keywords ====== */}
      <CollapsibleSection
        title={t('analysis.keywordSection')}
        desc={t('analysis.keywordSectionDesc')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {([
            { key: 'charKeyword' as const, label: t('analysis.charKeywordTitle'), value: charKeywordRules, setter: setCharKeywordRules },
            { key: 'sceneKeyword' as const, label: t('analysis.sceneKeywordTitle'), value: sceneKeywordRules, setter: setSceneKeywordRules },
            { key: 'propKeyword' as const, label: t('analysis.propKeywordTitle'), value: propKeywordRules, setter: setPropKeywordRules },
          ]).map(item => (
            <div className="card" key={item.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{item.label}</h3>
                <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset(item.key)}>
                  {t('analysis.resetDefault')}
                </button>
              </div>
              <textarea
                className="input textarea"
                style={{ minHeight: '140px', fontSize: '0.82rem' }}
                value={item.value}
                onChange={(e) => item.setter(e.target.value)}
              />
              {cardFooter(item.key)}
            </div>
          ))}
        </div>

        {assetKeywordFormatSpec && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>{t('analysis.keywordFormatTitle')}</h3>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.keywordFormatDesc')}
            </p>
            <textarea style={readonlyStyle} value={assetKeywordFormatSpec.trim()} readOnly rows={10} />
          </div>
        )}
      </CollapsibleSection>

      {/* ====== Character Casting & Styling Tags ====== */}
      <CollapsibleSection
        title={t('analysis.characterStyleSection')}
        desc={t('analysis.characterStyleSectionDesc')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {([
            { key: 'castingTags' as const, label: t('analysis.castingTagsTitle'), desc: t('analysis.castingTagsDesc'), value: castingTagsRules, setter: setCastingTagsRules },
            { key: 'stylingTags' as const, label: t('analysis.stylingTagsTitle'), desc: t('analysis.stylingTagsDesc'), value: stylingTagsRules, setter: setStylingTagsRules },
          ]).map(item => (
            <div className="card" key={item.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{item.label}</h3>
                <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset(item.key)}>
                  {t('analysis.resetDefault')}
                </button>
              </div>
              <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
                {item.desc}
              </p>
              <textarea
                className="input textarea"
                style={{ minHeight: '140px', fontSize: '0.82rem' }}
                value={item.value}
                onChange={(e) => item.setter(e.target.value)}
              />
              {cardFooter(item.key)}
            </div>
          ))}
        </div>

        {(castingTagsFormatSpec || stylingTagsFormatSpec) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1.5rem' }}>
            {([
              { key: 'castingTagsSpec', label: t('analysis.castingTagsFormatTitle'), desc: t('analysis.castingTagsFormatDesc'), value: castingTagsFormatSpec },
              { key: 'stylingTagsSpec', label: t('analysis.stylingTagsFormatTitle'), desc: t('analysis.stylingTagsFormatDesc'), value: stylingTagsFormatSpec },
            ]).filter(item => item.value).map(item => (
              <div className="card" key={item.key}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>{item.label}</h3>
                <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
                  {item.desc}
                </p>
                <textarea style={readonlyStyle} value={item.value.trim()} readOnly rows={12} />
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* ====== Casting AI Rank ====== */}
      <CollapsibleSection
        title={t('analysis.castingRankSection')}
        desc={t('analysis.castingRankSectionDesc')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {([
            { key: 'castingRank' as const, label: t('analysis.castingRankRulesTitle'), desc: t('analysis.castingRankRulesDesc'), value: castingRankRules, setter: setCastingRankRules },
            { key: 'castingRankSpec' as const, label: t('analysis.castingRankSpecTitle'), desc: t('analysis.castingRankSpecDesc'), value: castingRankFormatSpec, setter: setCastingRankFormatSpec },
          ]).map(item => (
            <div className="card" key={item.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{item.label}</h3>
                <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset(item.key)}>
                  {t('analysis.resetDefault')}
                </button>
              </div>
              <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
                {item.desc}
              </p>
              <textarea
                className="input textarea"
                style={{ minHeight: '160px', fontSize: '0.82rem' }}
                value={item.value}
                onChange={(e) => item.setter(e.target.value)}
              />
              {cardFooter(item.key)}
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* ====== Analytics Report Prompts ====== */}
      <CollapsibleSection
        title={t('analysis.analyticsPromptSection')}
        desc={t('analysis.analyticsPromptSectionDesc')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.analyticsSystemTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('analyticsSystem')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.analyticsSystemDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '200px', fontSize: '0.82rem' }}
              value={analyticsSystemPrompt}
              onChange={(e) => setAnalyticsSystemPrompt(e.target.value)}
            />
            {cardFooter('analyticsSystem')}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.analyticsUserTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('analyticsUser')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.analyticsUserDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '160px', fontSize: '0.82rem' }}
              value={analyticsUserPromptTemplate}
              onChange={(e) => setAnalyticsUserPromptTemplate(e.target.value)}
            />
            {cardFooter('analyticsUser')}
          </div>
        </div>
      </CollapsibleSection>

      {/* ====== Reference Translation ====== */}
      <CollapsibleSection
        title={t('analysis.refPromptTitle')}
        desc={t('analysis.refPromptDesc')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.refPromptTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('refPrompt')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.refPromptDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '140px', fontSize: '0.82rem' }}
              value={refTranslationPrompt}
              onChange={(e) => setRefTranslationPrompt(e.target.value)}
            />
            {cardFooter('refPrompt')}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.refLangsTitle')}</h3>
              <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleReset('refLangs')}>
                {t('analysis.resetDefault')}
              </button>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.refLangsDesc')}
            </p>
            <textarea
              className="input textarea"
              style={{ minHeight: '100px', fontSize: '0.82rem' }}
              value={refTranslationLangs}
              onChange={(e) => setRefTranslationLangs(e.target.value)}
            />
            {cardFooter('refLangs')}
          </div>
        </div>
      </CollapsibleSection>

      {/* ====== Deep Thinking Config ====== */}
      <CollapsibleSection
        title={t('analysis.thinkingSection')}
        desc={t('analysis.thinkingSectionDesc')}
      >
        <div className="card">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{t('analysis.thinkingBasicTitle')}</h3>
            {[
              { label: t('analysis.thinkingExtract'), value: thinkingExtract, setter: setThinkingExtract },
              { label: t('analysis.thinkingConsolidate'), value: thinkingConsolidate, setter: setThinkingConsolidate },
            ].map(item => (
              <label key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={item.value} onChange={e => item.setter(e.target.checked)} style={{ width: '16px', height: '16px' }} />
                {item.label}
              </label>
            ))}

            <h3 style={{ margin: '0.75rem 0 0 0', fontSize: '0.95rem' }}>{t('analysis.thinkingAdvancedTitle')}</h3>
            {[
              { label: t('analysis.thinkingAdvExtract'), value: thinkingAdvExtract, setter: setThinkingAdvExtract },
              { label: t('analysis.thinkingAdvMerge'), value: thinkingAdvMerge, setter: setThinkingAdvMerge },
              { label: t('analysis.thinkingAdvValidate'), value: thinkingAdvValidate, setter: setThinkingAdvValidate },
            ].map(item => (
              <label key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={item.value} onChange={e => item.setter(e.target.checked)} style={{ width: '16px', height: '16px' }} />
                {item.label}
              </label>
            ))}

            <h3 style={{ margin: '0.75rem 0 0 0', fontSize: '0.95rem' }}>{t('analysis.thinkingPromptTitle')}</h3>
            {[
              { label: t('analysis.thinkingPrompt'), value: thinkingPrompt, setter: setThinkingPrompt },
              { label: t('analysis.thinkingSupplement'), value: thinkingSupplement, setter: setThinkingSupplement },
              { label: t('analysis.thinkingStateMatch'), value: thinkingStateMatch, setter: setThinkingStateMatch },
              { label: t('analysis.thinkingAssetKeyword'), value: thinkingAssetKeyword, setter: setThinkingAssetKeyword },
            ].map(item => (
              <label key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={item.value} onChange={e => item.setter(e.target.checked)} style={{ width: '16px', height: '16px' }} />
                {item.label}
              </label>
            ))}
          </div>
          {cardFooter('extract')}
        </div>
      </CollapsibleSection>

      {/* Format definitions reference */}
      <CollapsibleSection
        title={t('analysis.formatRefSection')}
        desc={t('analysis.formatRefSectionDesc')}
      >
        {assetJSONSpec && (
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>{t('analysis.assetJsonFormatTitle')}</h3>
            <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
              {t('analysis.assetJsonFormatDesc')}
            </p>
            <textarea style={readonlyStyle} value={assetJSONSpec} readOnly rows={20} />
          </div>
        )}

        {promptFormat && (
            <div className="card">
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>{t('analysis.promptFormatTitle')}</h3>
              <p style={{ color: 'var(--text-3)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' }}>
                {t('analysis.promptFormatDesc')}
              </p>
              <textarea style={readonlyStyle} value={promptFormat.trim()} readOnly rows={14} />
            </div>
          )}
      </CollapsibleSection>

      <ConfirmDialog
        open={confirmReset}
        onCancel={() => setConfirmReset(false)}
        onConfirm={doReset}
        title={t('analysis.confirmResetTitle')}
        message={t('analysis.confirmResetMessage')}
        confirmText={t('analysis.confirmResetOk')}
      />
    </div>
  );
};

export default AnalysisConfig;
